import React, { useContext, useEffect, useMemo, useState } from 'react'
import 'react-dates/initialize'
import moment from 'moment'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/atoms/modal'
import { Button, ButtonType } from '@/components/atoms/button'
import { RadioButton } from '@/components/atoms/radio-button'
import { DateField } from '@/components/molecules/date-field'
import { PostClosingSelectableBusinessDatePicker } from '@/components/molecules/customer/sales-manager/usage-detail/post-closing-selectable-business-date-picker'
import { TimePicker } from '@/components/atoms/time-picker'
import { fetchSelectablePaymentBusinessDates } from '@/apis/aipass'
import { AccountContext } from '@/contexts/account'
import type { AccountContextValue } from '@/contexts/account'
import { canUseOperation } from '@/libs/hotel-pms-permission'
import type { PostClosingPaymentSource } from '@/libs/post-closing-sales-adjustment'
import type { PostClosingDateMode, PostClosingModalSubmitValues } from '@/models/post-closing-sales-adjustment'
import {
  buildSelectablePaymentBusinessDateSet,
  isSelectablePaymentBusinessDate,
  resolvePostClosingBusinessSalesDate,
  resolvePostClosingPaymentMoment,
} from '@/libs/post-closing-sales-adjustment'

const POST_CLOSING_TIME_DISPLAY_FORMAT = 'HH:mm'
const POST_CLOSING_DATE_VALUE_FORMAT = 'YYYY-MM-DD'

const formatPostClosingPaymentAtDisplay = (value: moment.Moment, t: (key: string) => string): string => {
  return value.format(t('YYYY-MM-DD HH:mm'))
}

const formatPostClosingBusinessDateDisplay = (value: moment.Moment, t: (key: string) => string): string => {
  return value.format(t('YYYY-MM-DD'))
}

const parseSalesDateValue = (value: string | undefined | null): moment.Moment | null => {
  if (!value) return null
  const strict = moment(value, POST_CLOSING_DATE_VALUE_FORMAT, true)
  if (strict.isValid()) return strict
  const loose = moment(value)
  return loose.isValid() ? loose : null
}

const POST_CLOSING_DATE_PICKER_Z_INDEX_STYLE = `
  .SingleDatePicker_picker__portal {
    z-index: 11110 !important;
  }
  .SingleDatePicker_picker {
    z-index: 11110 !important;
  }
  .CalendarDay__blocked_out_of_range .day,
  .CalendarDay__blocked_out_of_range:hover .day {
    color: #cacccd !important;
    background: none !important;
    pointer-events: none !important;
  }
  .CalendarDay__blocked_out_of_range:hover div {
    background: none !important;
    color: #cacccd !important;
  }
`

const getInitialVisibleMonth = (date: moment.Moment | null) => () => date?.clone() ?? moment()

type PostClosingPaymentDateModalProps = {
  usageDetail: PostClosingPaymentSource
  isSaving?: boolean
  onClose: () => void
  onSave: (values?: PostClosingModalSubmitValues) => void
}

export const PostClosingPaymentDateModal: React.FC<PostClosingPaymentDateModalProps> = ({
  usageDetail,
  isSaving = false,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation()
  const { permissions, permissionsLoaded } = useContext<AccountContextValue>(AccountContext)
  const [selectablePaymentBusinessDates, setSelectablePaymentBusinessDates] = useState<Set<string>>(new Set())
  const initialPaymentMoment = useMemo(() => {
    const resolved = resolvePostClosingPaymentMoment(usageDetail)
    return resolved?.isValid() ? resolved : null
  }, [usageDetail])
  const originalBusinessSalesDate = useMemo(() => resolvePostClosingBusinessSalesDate(usageDetail), [usageDetail])

  // 売上画面の先読みはしない。モーダル表示時かつ post_closing_sales_edit があるときだけ取得する。
  useEffect(() => {
    if (!permissionsLoaded) return
    if (!canUseOperation(permissions, 'post_closing_sales_edit')) {
      setSelectablePaymentBusinessDates(new Set())
      return
    }
    let cancelled = false
    fetchSelectablePaymentBusinessDates()
      .then(response => {
        if (cancelled) return
        setSelectablePaymentBusinessDates(buildSelectablePaymentBusinessDateSet(response?.items ?? []))
      })
      .catch(() => {
        if (cancelled) return
        setSelectablePaymentBusinessDates(new Set())
      })
    return () => {
      cancelled = true
    }
  }, [permissions, permissionsLoaded])
  const [dateMode, setDateMode] = useState<PostClosingDateMode>('paymentAt')
  const [paymentDate, setPaymentDate] = useState<moment.Moment | null>(null)
  const [paymentTime, setPaymentTime] = useState('00:00')
  const [salesDate, setSalesDate] = useState<moment.Moment | null>(null)

  useEffect(() => {
    setPaymentDate(initialPaymentMoment?.clone().startOf('day') ?? null)
    setPaymentTime(initialPaymentMoment?.format(POST_CLOSING_TIME_DISPLAY_FORMAT) ?? '00:00')
    setSalesDate(parseSalesDateValue(originalBusinessSalesDate))
  }, [initialPaymentMoment, originalBusinessSalesDate])

  const initialSelectablePaymentBusinessDate = useMemo(() => {
    const sorted = Array.from(selectablePaymentBusinessDates).sort()
    return sorted.length > 0 ? moment(sorted[0], POST_CLOSING_DATE_VALUE_FORMAT) : null
  }, [selectablePaymentBusinessDates])

  const hasSelectablePaymentBusinessDates = selectablePaymentBusinessDates.size > 0
  const selectedPaymentBusinessDate = salesDate?.format(POST_CLOSING_DATE_VALUE_FORMAT)
  const isSelectedPaymentBusinessDateValid =
    Boolean(selectedPaymentBusinessDate) && isSelectablePaymentBusinessDate(selectedPaymentBusinessDate!, selectablePaymentBusinessDates)

  const modalTitle = t('HotelPms.PostClosing.changePaymentDateTitle')

  const parsedBusinessSalesDate = parseSalesDateValue(originalBusinessSalesDate)
  const beforeValueText =
    dateMode === 'paymentAt'
      ? initialPaymentMoment
        ? formatPostClosingPaymentAtDisplay(initialPaymentMoment, t)
        : '-'
      : parsedBusinessSalesDate
      ? formatPostClosingBusinessDateDisplay(parsedBusinessSalesDate, t)
      : '-'

  const handleSaveWithoutPaymentChange = () => {
    onSave()
  }

  const handleSave = () => {
    if (dateMode === 'paymentAt') {
      if (!paymentDate) return
      onSave({
        dateMode,
        paymentAtUnixtime: moment(
          `${paymentDate.format(POST_CLOSING_DATE_VALUE_FORMAT)} ${paymentTime || '00:00'}`,
          `${POST_CLOSING_DATE_VALUE_FORMAT} ${POST_CLOSING_TIME_DISPLAY_FORMAT}`,
          true,
        ).unix(),
      })
      return
    }
    if (!salesDate || !isSelectedPaymentBusinessDateValid) {
      alert(t('HotelPms.PostClosing.noSelectablePaymentBusinessDates'))
      return
    }
    onSave({
      dateMode,
      paymentBusinessSalesDate: salesDate.format(POST_CLOSING_DATE_VALUE_FORMAT),
    })
  }

  const isBusinessDateSaveDisabled = dateMode === 'salesDate' && (!hasSelectablePaymentBusinessDates || !isSelectedPaymentBusinessDateValid)

  return (
    <Modal customCss={modalStyle} containerCss={css({ display: 'flex' })} isLoading={isSaving}>
      <style>{POST_CLOSING_DATE_PICKER_Z_INDEX_STYLE}</style>
      <ModalHeader>{modalTitle}</ModalHeader>
      <ModalBody customCss={modalBodyStyle}>
        <div css={radioGroupStyle}>
          <RadioButton
            id="post-closing-date-mode-payment-at"
            name="postClosingDateMode"
            checked={dateMode === 'paymentAt'}
            item={{ value: 'paymentAt', label: t('HotelPms.PostClosing.paymentDateTime') }}
            onChange={() => setDateMode('paymentAt')}
            itemCss={radioItemStyle}
            labelCss={radioLabelTextStyle}
            style={{ radioSize: 18, labelMargin: 8 }}
          />
          <RadioButton
            id="post-closing-date-mode-sales-date"
            name="postClosingDateMode"
            checked={dateMode === 'salesDate'}
            item={{ value: 'salesDate', label: t('HotelPms.PostClosing.businessDate') }}
            onChange={() => setDateMode('salesDate')}
            itemCss={radioItemStyle}
            labelCss={radioLabelTextStyle}
            style={{ radioSize: 18, labelMargin: 8 }}
          />
        </div>

        <div css={changeRowStyle}>
          <span css={beforeValueStyle}>{beforeValueText}</span>
          <svg width="7" height="15" viewBox="0 0 7 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden css={changeArrowStyle}>
            <path d="M1.5 1.125 5.25 7.5 1.5 13.875" stroke="#676767" strokeWidth="3" strokeLinecap="butt" strokeLinejoin="miter" />
          </svg>
          <div css={afterFieldsStyle}>
            {dateMode === 'paymentAt' ? (
              <div css={dateInputRowStyle}>
                <DateField
                  key="post-closing-payment-date"
                  id="post-closing-payment-date"
                  value={paymentDate}
                  onChange={date => setPaymentDate(date)}
                  displayFormat={t('YYYY-MM-DD')}
                  withPortal={true}
                  enableOutsideDays={false}
                  initialVisibleMonth={getInitialVisibleMonth(paymentDate)}
                  placeholder={t('YYYY-MM-DD')}
                />
                <div css={timePickerWrapperStyle}>
                  <TimePicker
                    minuteStep={1}
                    value={moment(paymentTime || '00:00', POST_CLOSING_TIME_DISPLAY_FORMAT, true) as any}
                    onChange={value => setPaymentTime(value?.format(POST_CLOSING_TIME_DISPLAY_FORMAT) ?? '00:00')}
                    getPopupContainer={() => document.body}
                  />
                </div>
              </div>
            ) : (
              <>
                <PostClosingSelectableBusinessDatePicker
                  date={salesDate}
                  onChange={setSalesDate}
                  selectablePaymentBusinessDates={selectablePaymentBusinessDates}
                  initialVisibleMonth={getInitialVisibleMonth(salesDate ?? initialSelectablePaymentBusinessDate)}
                />
                {!hasSelectablePaymentBusinessDates && (
                  <p css={helperTextStyle}>{t('HotelPms.PostClosing.noSelectablePaymentBusinessDates')}</p>
                )}
              </>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter customCss={modalFooterStyle}>
        <Button
          buttonType={ButtonType.Secondary}
          height={38}
          width={110}
          fontSize={14}
          fontWeight="bold"
          borderRadius={23}
          customCss={modalFooterSecondaryButtonTextStyle}
          onClick={onClose}
        >
          {t('Cancel')}
        </Button>
        <div css={footerActionsRightStyle}>
          <Button
            buttonType={ButtonType.Secondary}
            height={38}
            width={130}
            fontSize={14}
            fontWeight="bold"
            borderRadius={23}
            marginRight={16}
            customCss={modalFooterSecondaryButtonTextStyle}
            onClick={handleSaveWithoutPaymentChange}
          >
            {t('HotelPms.PostClosing.saveWithoutPaymentChange')}
          </Button>
          <Button
            buttonType={ButtonType.Primary}
            height={38}
            width={109}
            fontSize={14}
            fontWeight="bold"
            borderRadius={23}
            customCss={modalFooterPrimaryButtonTextStyle}
            onClick={handleSave}
            isDisabled={isBusinessDateSaveDisabled}
          >
            {t('Save')}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

const modalStyle = css({
  width: 680,
  height: 'auto',
  margin: 'auto',
})

const modalBodyStyle = css({
  padding: '26px 32px',
  backgroundColor: '#F2F2F2',
  overflow: 'visible',
})

const modalFooterStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
})

const footerActionsRightStyle = css({
  display: 'flex',
  alignItems: 'center',
})

const radioGroupStyle = css({
  display: 'flex',
  gap: 24,
  marginBottom: 16,
  alignItems: 'center',
})

const radioItemStyle = css({
  alignItems: 'center',
  color: '#676767',
})

const radioLabelTextStyle = css({
  display: 'block',
  fontFamily: 'YuGothic, "Yu Gothic", sans-serif',
  fontSize: 12,
  fontWeight: 'bold',
  lineHeight: '32px',
  letterSpacing: '0.6px',
  color: '#676767',
  cursor: 'pointer',
})

const changeRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  minWidth: 0,
})

const beforeValueStyle = css({
  flex: '0 0 auto',
  fontFamily: 'YuGothic, "Yu Gothic", sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '21px',
  letterSpacing: '1.4px',
  color: '#272727',
  textAlign: 'left',
  whiteSpace: 'nowrap',
})

const changeArrowStyle = css({
  display: 'block',
  flexShrink: 0,
  width: '7px',
  height: '15px',
  opacity: 1,
})

const afterFieldsStyle = css({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'visible',
  '.SingleDatePicker_picker': {
    zIndex: 11110,
  },
  '.ant-time-picker-panel': {
    zIndex: 11110,
  },
})

const dateInputRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
})

const timePickerWrapperStyle = css({
  flexShrink: 0,
  width: 107,
})

const helperTextStyle = css({
  margin: '8px 0 0',
  fontSize: 11,
  color: '#676767',
})

const modalFooterSecondaryButtonTextStyle = css({
  fontFamily: 'YuGothic, "Yu Gothic", sans-serif',
  letterSpacing: '2.1px',
  color: '#F2A40B',
})

const modalFooterPrimaryButtonTextStyle = css({
  fontFamily: 'YuGothic, "Yu Gothic", sans-serif',
  letterSpacing: '2.1px',
  color: '#FFFFFF',
})
